import { useState, useMemo, useEffect } from "react";
import { DollarSign, Search, Check, X, TrendingUp, Lock, Percent } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { useAdminData } from "../../hooks/useAdminData";
import { Btn } from "../../components/ui/Btn";
import { Card } from "../../components/ui/Card";
import { KpiCard } from "../../components/ui/KpiCard";
import { PageHeader } from "../../components/ui/PageHeader";
import { CORAL, F, T } from "../../theme/tokens";
import { TableWrap } from "../../components/ui/TableWrap";

// ── Finance Analytics ─────────────────────────────────────────────────────────

export function getPeriodData(monthlyRevenue: { month: string; amount: number }[]): Record<string, { revenue: string; fees: string; payouts: string; escrow: string; trend: string; revenueData: { month: string; amount: number; fees: number }[] }> {
  return {
    "7d": {
      revenue: "SAR 218,400", fees: "SAR 21,840", payouts: "SAR 196,560", escrow: "SAR 84,200", trend: "+3.1%", revenueData: [
        { month: "Mon", amount: 28000, fees: 2800 }, { month: "Tue", amount: 31000, fees: 3100 }, { month: "Wed", amount: 24000, fees: 2400 },
        { month: "Thu", amount: 36000, fees: 3600 }, { month: "Fri", amount: 41000, fees: 4100 }, { month: "Sat", amount: 22000, fees: 2200 }, { month: "Sun", amount: 36400, fees: 3640 },
      ]
    },
    "30d": { revenue: "SAR 942,000", fees: "SAR 94,200", payouts: "SAR 847,800", escrow: "SAR 210,400", trend: "+9.4%", revenueData: monthlyRevenue.slice(-4).map((m, i) => ({ month: `W${i + 1}`, amount: Math.round(m.amount / 4), fees: Math.round(m.amount * 0.1 / 4) })) },
    "90d": { revenue: "SAR 2.76M", fees: "SAR 276K", payouts: "SAR 2.48M", escrow: "SAR 380,000", trend: "+11.2%", revenueData: monthlyRevenue.slice(-3).map((m) => ({ ...m, fees: Math.round(m.amount * 0.1) })) },
    "12m": { revenue: "SAR 9.4M", fees: "SAR 940K", payouts: "SAR 8.46M", escrow: "SAR 420,000", trend: "+14.2%", revenueData: monthlyRevenue.map((m) => ({ ...m, fees: Math.round(m.amount * 0.1) })) },
    "custom": { revenue: "SAR 1.84M", fees: "SAR 184K", payouts: "SAR 1.65M", escrow: "SAR 295,000", trend: "+7.8%", revenueData: monthlyRevenue.slice(-6).map((m) => ({ ...m, fees: Math.round(m.amount * 0.1) })) },
  };
}

export function AnalyticsView() {
  const { MONTHLY_REVENUE, ADMIN_TALENTS, ADMIN_ORGS, ADMIN_PAYMENTS } = useAdminData();
  const [period, setPeriod] = useState("12m");
  const [customFrom, setCustomFrom] = useState("2026-01-01");
  const [customTo, setCustomTo] = useState("2026-09-14");
  const [showCustom, setShowCustom] = useState(false);

  const PERIOD_DATA = useMemo(() => getPeriodData(MONTHLY_REVENUE), [MONTHLY_REVENUE]);
  const data = PERIOD_DATA[period] ?? PERIOD_DATA["12m"];
  const periods = ["7d", "30d", "90d", "12m"];

  const talentBalanceRows = ADMIN_TALENTS.map((t) => ({ id: t.id, name: t.name, initials: t.initials, color: t.color, col1: t.walletBalance, col2: t.pendingPayout, col1Label: "Wallet", col2Label: "Pending", isOrg: false }));
  const orgBalanceRows = ADMIN_ORGS.map((o) => ({ id: o.id, name: o.name, initials: o.initials, color: o.color, col1: o.walletBalance, col2: o.escrowHeld, col1Label: "Wallet", col2Label: "Escrow", isOrg: true }));

  return (
    <div>
      <PageHeader title="Finance Analytics" sub="Revenue, balances, and platform earnings" stackOnPhone actions={
        <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
          {periods.map((p) => (
            <button key={p} onClick={() => { setPeriod(p); setShowCustom(false); }}
              style={{ padding: "5px 12px", borderRadius: "6px", fontSize: "11px", fontFamily: F, fontWeight: 500, cursor: "pointer", border: `1px solid ${period === p && !showCustom ? T.text : T.border}`, background: period === p && !showCustom ? T.text : T.surface, color: period === p && !showCustom ? "#fff" : T.sub, transition: "all 0.15s" }}>
              {p}
            </button>
          ))}
          <button onClick={() => { setShowCustom((v) => !v); if (!showCustom) setPeriod("custom"); }}
            style={{ padding: "5px 12px", borderRadius: "6px", fontSize: "11px", fontFamily: F, fontWeight: 500, cursor: "pointer", border: `1px solid ${showCustom ? T.text : T.border}`, background: showCustom ? T.text : T.surface, color: showCustom ? "#fff" : T.sub, display: "flex", alignItems: "center", gap: "5px" }}>
            Custom range
          </button>
        </div>
      } />

      {showCustom && (
        <Card style={{ padding: "16px 20px", marginBottom: "20px" }}>
          <div style={{ display: "flex", gap: "14px", alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "12px", color: T.sub, fontFamily: F }}>From</span>
              <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
                style={{ border: `1px solid ${T.border}`, borderRadius: "7px", padding: "6px 10px", fontSize: "12px", fontFamily: F, outline: "none", color: T.text }} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "12px", color: T.sub, fontFamily: F }}>To</span>
              <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
                style={{ border: `1px solid ${T.border}`, borderRadius: "7px", padding: "6px 10px", fontSize: "12px", fontFamily: F, outline: "none", color: T.text }} />
            </div>
            <Btn variant="primary" size="sm" onClick={() => { }}><Check size={12} /> Apply</Btn>
          </div>
        </Card>
      )}

      {/* Revenue KPIs */}
      <div className="rg-4" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "14px", marginBottom: "24px" }}>
        <KpiCard icon={<TrendingUp size={16} />} label="Gross revenue" value={data.revenue} trend={data.trend} trendUp />
        <KpiCard icon={<Percent size={16} />} label="Platform fees earned" value={data.fees} sub="10% avg fee" trend={data.trend} trendUp />
        <KpiCard icon={<DollarSign size={16} />} label="Talent payouts" value={data.payouts} />
        <KpiCard icon={<Lock size={16} />} label="In escrow" value={data.escrow} sub="Active projects" />
      </div>

      {/* Revenue chart */}
      <div className="rg-split" style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "14px", marginBottom: "24px" }}>
        <Card style={{ padding: "20px 22px" }}>
          <p style={{ fontSize: "13px", fontWeight: 600, color: T.text, fontFamily: F, marginBottom: "16px" }}>Revenue vs Platform fees (SAR)</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.revenueData} margin={{ top: 4, right: 4, bottom: 0, left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
              <XAxis dataKey="month" tick={{ fontSize: 9, fill: T.muted, fontFamily: F }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 9, fill: T.muted, fontFamily: F }} tickLine={false} axisLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`} />
              <Tooltip contentStyle={{ fontFamily: F, fontSize: "11px", borderRadius: "8px", border: `1px solid ${T.border}` }} formatter={(v, name) => [`SAR ${Number(v).toLocaleString()}`, name === "amount" ? "Gross revenue" : "Platform fees"]} />
              <Bar dataKey="amount" fill={T.text} radius={[3, 3, 0, 0]} name="amount" />
              <Bar dataKey="fees" fill={CORAL} radius={[3, 3, 0, 0]} name="fees" />
              <Legend iconType="circle" iconSize={7} wrapperStyle={{ fontSize: "10px", fontFamily: F }} formatter={(v) => v === "amount" ? "Gross revenue" : "Platform fees"} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card style={{ padding: "20px 22px" }}>
          <p style={{ fontSize: "13px", fontWeight: 600, color: T.text, fontFamily: F, marginBottom: "16px" }}>Revenue breakdown</p>
          <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
            {[
              { label: "Gross revenue", value: data.revenue, highlight: false },
              { label: "Platform fees (10%)", value: data.fees, highlight: true },
              { label: "Talent payouts", value: data.payouts, highlight: false },
              { label: "Escrow held", value: data.escrow, highlight: false },
              { label: "Avg. transaction", value: "SAR 12,480", highlight: false },
              { label: "Transactions count", value: ADMIN_PAYMENTS.length.toString(), highlight: false },
            ].map((item, i, arr) => (
              <div key={item.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "11px 0", borderBottom: i < arr.length - 1 ? `1px solid ${T.border}` : "none" }}>
                <p style={{ fontSize: "12px", color: T.sub, fontFamily: F }}>{item.label}</p>
                <p style={{ fontSize: "13px", fontWeight: 700, color: item.highlight ? CORAL : T.text, fontFamily: F }}>{item.value}</p>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Talent + Org balances with search + pagination */}
      <div className="rg-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
        <BalanceTable title="Talent wallet balances" rows={talentBalanceRows} />
        <BalanceTable title="Organisation balances" rows={orgBalanceRows} />
      </div>
    </div>
  );
}

export function BalanceTable({ title, rows }: { title: string; rows: { id: string; name: string; initials: string; color: string; col1: string; col2: string; col1Label: string; col2Label: string; isOrg: boolean }[] }) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 6;

  const filtered = rows.filter((r) => !search || r.name.toLowerCase().includes(search.toLowerCase()));
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageRows = filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  useEffect(() => { setPage(0); }, [search]);

  return (
    <Card className="flat-phone" style={{ padding: "20px 22px", marginBottom: "14px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px", gap: "12px", flexWrap: "wrap" }}>
        <p style={{ fontSize: "13px", fontWeight: 600, color: T.text, fontFamily: F, margin: 0 }}>{title}</p>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", background: T.s2, border: `1px solid ${T.border}`, borderRadius: "7px", padding: "6px 10px" }}>
          <Search size={12} style={{ color: T.muted, flexShrink: 0 }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…"
            style={{ border: "none", outline: "none", background: "transparent", fontSize: "11px", fontFamily: F, color: T.text, width: "140px" }} />
          {search && <button onClick={() => setSearch("")} style={{ background: "none", border: "none", cursor: "pointer", color: T.muted, lineHeight: 0 }}><X size={10} /></button>}
        </div>
      </div>
      <TableWrap><table style={{ width: "100%", borderCollapse: "collapse", fontFamily: F }}>
        <thead>
          <tr style={{ borderBottom: `1px solid ${T.border}` }}>
            {["Name", rows[0]?.col1Label ?? "Balance", rows[0]?.col2Label ?? "Pending"].map((h) => (
              <th key={h} style={{ padding: "7px 10px", textAlign: "left", fontSize: "10px", fontWeight: 700, color: T.muted, letterSpacing: "0.06em", textTransform: "uppercase" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {pageRows.map((r, i) => (
            <tr key={r.id} style={{ borderBottom: i < pageRows.length - 1 ? `1px solid ${T.border}` : "none" }}>
              <td style={{ padding: "9px 10px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
                  <div style={{ width: 24, height: 24, borderRadius: r.isOrg ? "6px" : "50%", background: r.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <span style={{ fontSize: "8px", fontWeight: 700, color: "#fff", fontFamily: F }}>{r.initials}</span>
                  </div>
                  <p style={{ fontSize: "12px", fontWeight: 500, color: T.text }}>{r.name}</p>
                </div>
              </td>
              <td style={{ padding: "9px 10px", fontSize: "12px", fontWeight: 600, color: r.col1 === "SAR 0" ? T.muted : "#166534" }}>{r.col1}</td>
              <td style={{ padding: "9px 10px", fontSize: "11px", color: r.col2 === "SAR 0" ? T.muted : "#92400e" }}>{r.col2}</td>
            </tr>
          ))}
          {pageRows.length === 0 && (
            <tr><td colSpan={3} style={{ padding: "24px", textAlign: "center", color: T.muted, fontSize: "12px", fontFamily: F }}>No results.</td></tr>
          )}
        </tbody>
      </table></TableWrap>
      {totalPages > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "12px", paddingTop: "10px", borderTop: `1px solid ${T.border}` }}>
          <p style={{ fontSize: "10px", color: T.muted, fontFamily: F }}>Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} of {filtered.length}</p>
          <div style={{ display: "flex", gap: "4px" }}>
            <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
              style={{ padding: "4px 10px", borderRadius: "6px", fontSize: "11px", fontFamily: F, border: `1px solid ${T.border}`, background: T.surface, color: T.sub, cursor: page === 0 ? "not-allowed" : "pointer", opacity: page === 0 ? 0.4 : 1 }}>
              ←
            </button>
            {Array.from({ length: totalPages }).map((_, i) => (
              <button key={i} onClick={() => setPage(i)}
                style={{ padding: "4px 10px", borderRadius: "6px", fontSize: "11px", fontFamily: F, border: `1px solid ${i === page ? T.text : T.border}`, background: i === page ? T.text : T.surface, color: i === page ? "#fff" : T.sub, cursor: "pointer" }}>
                {i + 1}
              </button>
            ))}
            <button onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
              style={{ padding: "4px 10px", borderRadius: "6px", fontSize: "11px", fontFamily: F, border: `1px solid ${T.border}`, background: T.surface, color: T.sub, cursor: page >= totalPages - 1 ? "not-allowed" : "pointer", opacity: page >= totalPages - 1 ? 0.4 : 1 }}>
              →
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}
